import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Accordion, AccordionDetails, AccordionSummary, Badge, Box, Button, CircularProgress, Collapse, Divider, Drawer, FormControlLabel, Grid, IconButton, Link, List, ListItem, Switch, SwipeableDrawer, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SubjectIcon from '@mui/icons-material/Subject';
import { grey } from '@mui/material/colors';

import {
    eventExtended,
    fetchEventsByHandles,
    fetchFamiliesByHandles,
    fetchPeopleByHandles,
    fetchPersonByGrampsId,
    fetchRelationship,
    mapRestMedia,
    personExtended,
    restDateToJsDate,
    restEventDateString,
    restEventPrincipals,
    isAttendanceRole,
    ATTENDANCE_ROLES,
    type AttendanceRole,
    restPersonIsMale,
    restPersonName,
    toGrampsNote,
    type EventParticipant,
    type RestEvent,
    type RestFamily,
    type RestPerson,
} from '../api/grampsRest';
import { getGrampsBackendBase, isMarchandViveretHost, readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import { useDrawerResize } from '../hooks/useDrawerResize';
import { useAncestorFamilies, useAncestorPersons } from '../hooks/useNotableEvents';
import { frenchEvents } from '../utils';
import { getStoryPath, useViewRoute } from '../viewRoute';
import { MapTiles } from './MapTiles';
import { boundsOfFeatures, curvedStepFeatures } from './mapCurves';
import { MediaList } from './MediaList';
import { NoteItem } from './NoteItem';
import { RestEventItem } from './RestEventItem';
import { PersonStories } from '../stories/StoryPages';

const MIN_LEFT_PANE_WIDTH = 280;
// Kept in sync with MAX_LEFT_PANE_WIDTH_VW in App.tsx.
const MAX_LEFT_PANE_WIDTH_RATIO = 0.6;
const MOBILE_TOOLBAR_HEIGHT = 56;
const COLLAPSED_HEIGHT = 34;
/** Open, the sheet stops short of the app bar: the dimmed page shows above
 * it, so it reads as a sheet laid over the page, one that can slide away. */
const MOBILE_OPEN_GAP = 24;
const SHEET_RADIUS = 16;
const Puller = styled('div')({ width: 40, height: 5, backgroundColor: grey[400], borderRadius: 3, position: 'absolute', top: 'calc(50% - 2px)', left: 'calc(50% - 20px)' });

/** An event the selected person attended as a witness, informant or godparent,
 * with whom it was about. */
type AttendedEntry = {
    event: RestEvent;
    role: AttendanceRole;
    principals: { person: EventParticipant; relation?: string }[];
};

function attendedSectionTitle(role: AttendanceRole, person: RestPerson): string {
    if (role === 'Witness') return 'Témoin à';
    if (role === 'Informant') return 'Déclarant à';
    return person.gender === 1 ? 'Parrain de' : person.gender === 0 ? 'Marraine de' : 'Parrain ou marraine de';
}

/** What being at an event made the person, on its map marker. */
function attendanceLabel(role: AttendanceRole, person: RestPerson): string {
    if (role === 'Witness') return 'Témoin';
    if (role === 'Informant') return 'Déclarant';
    return person.gender === 1 ? 'Parrain' : person.gender === 0 ? 'Marraine' : 'Parrain ou marraine';
}

type EventEntry = {
    event: RestEvent;
    who: 'own' | 'child';
    isFamily: boolean;
    isSubjectFamily?: boolean;
    relatedPerson?: RestPerson;
    relatedLabel?: string;
};

function getDefaultLeftPaneWidth() {
    if (typeof window === 'undefined') return 380;
    const preferred = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue('--event-drawer-default-width'), 10) || 380;
    // useDrawerResize only clamps while dragging, so clamp the initial width too:
    // otherwise the default leaves almost no room for the view on a narrow desktop.
    return Math.max(MIN_LEFT_PANE_WIDTH, Math.min(preferred, Math.floor(window.innerWidth * MAX_LEFT_PANE_WIDTH_RATIO)));
}

function unique(values: Array<string | undefined>) {
    return [...new Set(values.filter((value): value is string => !!value))];
}

const showBackendLinks = !isMarchandViveretHost();

function backendUrl(kind: string, id: string) {
    return `${getGrampsBackendBase().replace(/\/+$/, '')}/${kind}/${encodeURIComponent(id)}`;
}

function parentScore(family: RestFamily, childHandle: string | undefined) {
    const ref = (family.child_ref_list ?? []).find((child) => child.ref === childHandle);
    return Number(ref?.frel?.toLowerCase() === 'birth') + Number(ref?.mrel?.toLowerCase() === 'birth');
}

function bestParentFamily(person: RestPerson, families: RestFamily[]) {
    return families
        .filter((family) => family.child_ref_list?.some((child) => child.ref === person.handle))
        .sort((first, second) => parentScore(second, person.handle) - parentScore(first, person.handle))[0];
}

function relationshipLabel(selected: RestPerson, relative: RestPerson, families: RestFamily[]) {
    const selectedHandle = selected.handle;
    const relativeHandle = relative.handle;
    if (!selectedHandle || !relativeHandle) return undefined;

    if (families.some((family) => family.child_ref_list?.some((child) => child.ref === selectedHandle) && family.father_handle === relativeHandle)) return 'son père';
    if (families.some((family) => family.child_ref_list?.some((child) => child.ref === selectedHandle) && family.mother_handle === relativeHandle)) return 'sa mère';
    if (families.some((family) => (family.father_handle === selectedHandle || family.mother_handle === selectedHandle) && family.child_ref_list?.some((child) => child.ref === relativeHandle))) return restPersonIsMale(relative) ? 'son fils' : 'sa fille';
    if (families.some((family) => (family.father_handle === selectedHandle && family.mother_handle === relativeHandle) || (family.mother_handle === selectedHandle && family.father_handle === relativeHandle))) return restPersonIsMale(relative) ? 'son époux' : 'son épouse';
    if (families.some((family) => family.child_ref_list?.some((child) => child.ref === selectedHandle) && family.child_ref_list?.some((child) => child.ref === relativeHandle))) return restPersonIsMale(relative) ? 'son frère' : 'sa sœur';
    return undefined;
}

function isRelativeEvent(entry: EventEntry) {
    return !!entry.relatedPerson && !entry.isSubjectFamily;
}

function eventCoordinates(event: RestEvent): [number, number] | undefined {
    const place = eventExtended(event).place;
    const longitude = Number.parseFloat(place?.long ?? '');
    const latitude = Number.parseFloat(place?.lat ?? '');
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return undefined;
    return longitude >= -180 && longitude <= 180 && latitude >= -90 && latitude <= 90 ? [longitude, latitude] : undefined;
}

function eventOccursDuringLife(event: RestEvent, birthDate: Date | undefined, deathDate: Date | undefined) {
    const eventDate = restDateToJsDate(event.date);
    if (!eventDate) return false;
    return (!birthDate || eventDate >= birthDate) && (!deathDate || eventDate <= deathDate);
}

function personEventHandle(person: RestPerson, refIndex: number | undefined, type: string) {
    const index = typeof refIndex === 'number' && refIndex >= 0 ? refIndex : undefined;
    const extendedEvents = personExtended(person).events ?? [];
    return index === undefined
        ? extendedEvents.find((event) => event.type === type)?.handle
        : person.event_ref_list?.[index]?.ref
            ?? extendedEvents[index]?.handle
            ?? extendedEvents.find((event) => event.type === type)?.handle;
}

function deathEventHandle(person: RestPerson) {
    return personEventHandle(person, person.death_ref_index, 'Death');
}

function birthEventHandle(person: RestPerson) {
    return personEventHandle(person, person.birth_ref_index, 'Birth');
}

function baptismEventHandle(person: RestPerson) {
    return personEventHandle(person, undefined, 'Baptism');
}

function renderParentButton(handle: string | undefined, people: Map<string, RestPerson>, setSideItemId: (personId: string) => void) {
    const parent = handle ? people.get(handle) : undefined;
    return <Button variant={parent ? 'contained' : 'outlined'} disabled={!parent} onClick={() => parent?.gramps_id && setSideItemId(parent.gramps_id)}>
        {parent ? restPersonName(parent) : 'Inconnu'}
    </Button>;
}

function personQueryKey(treeName: string | null, personId: string) {
    return ['person', treeName, personId] as const;
}

function ownDetailsQueryKey(treeName: string | null, mainId: string, selectedId: string) {
    return ['event-drawer', treeName, mainId, selectedId] as const;
}

function relatedEntriesQueryKey(treeName: string | null, mainId: string, selectedId: string) {
    return ['event-drawer-related', treeName, mainId, selectedId] as const;
}

/**
 * The fast half of the drawer's data: the selected person's own timeline (their
 * events, plus the family events — marriages, etc. — they are a parent or spouse
 * in) and everything needed to resolve it. Two round trips once `selected` is
 * known: relatives and the selected person's own/family events fetch in
 * parallel, since neither depends on the other.
 *
 * Deliberately excludes relatives' *own* events (their births, deaths): those
 * need a third, dependent round trip, since which handles to fetch is only
 * known once this step's own relatives fetch resolves. `loadRelatedDrawerEntries`
 * fetches those separately and the caller merges them in once they arrive,
 * instead of holding up the drawer's first paint on the slowest step.
 *
 * main/selected are re-fetched here (through the query client, so a fresh cache entry
 * is reused rather than duplicated) instead of being passed in from the caller's render.
 * When the transaction poller invalidates every query at once, this query's own refetch
 * can otherwise run before the sibling person queries have resolved, capturing their
 * *previous* event_ref_list in a closure — new events on the person would then never
 * make it into the handles this function fetches, even though edits to already-known
 * events would still show up.
 */
async function loadOwnDrawerDetails(queryClient: QueryClient, treeName: string | null, mainId: string, selectedId: string, token: string | undefined) {
    const [main, selected] = await Promise.all([
        queryClient.fetchQuery({ queryKey: personQueryKey(treeName, mainId), queryFn: () => fetchPersonByGrampsId(mainId, token) }),
        queryClient.fetchQuery({ queryKey: personQueryKey(treeName, selectedId), queryFn: () => fetchPersonByGrampsId(selectedId, token) }),
    ]);
    if (!main || !selected) return undefined;

    const selectedExtension = personExtended(selected);
    const localFamilyHandles = unique([...(selected.parent_family_list ?? []), ...(selected.family_list ?? [])]);
    // Normally already extended onto the person; the fetch is only a safety net.
    const extendedFamilies = [...(selectedExtension.parent_families ?? []), ...(selectedExtension.families ?? [])];
    const localFamilies = extendedFamilies.length > 0 || localFamilyHandles.length === 0
        ? extendedFamilies
        : await fetchFamiliesByHandles(localFamilyHandles, token);

    const relativeHandles = unique(localFamilies.flatMap((family) => [family.father_handle, family.mother_handle, ...(family.child_ref_list ?? []).map((child) => child.ref)]));
    const ownFamilies = localFamilies.filter((family) => family.father_handle === selected.handle || family.mother_handle === selected.handle);
    const ownChildrenHandles = new Set(unique(ownFamilies.flatMap((family) => (family.child_ref_list ?? []).map((child) => child.ref))));
    const parentFamilies = localFamilies.filter((family) => family.child_ref_list?.some((child) => child.ref === selected.handle));
    const siblingHandles = new Set(unique(parentFamilies.flatMap((family) => (family.child_ref_list ?? []).map((child) => child.ref))).filter((handle) => handle !== selected.handle));
    const selectedDeathHandle = deathEventHandle(selected);
    // Events the person only attended (witness, informant, godparent): listed
    // apart, never in their own timeline.
    const ownRefHandles = new Set((selected.event_ref_list ?? []).filter((ref) => !isAttendanceRole(ref.role)).map((ref) => ref.ref));
    const attendedRoles = new Map((selected.event_ref_list ?? []).flatMap((ref) =>
        ref.ref && isAttendanceRole(ref.role) && !ownRefHandles.has(ref.ref) ? [[ref.ref, ref.role] as const] : []));
    const selectedEventHandles = unique([
        ...(selected.event_ref_list ?? []).map((ref) => ref.ref),
        ...(selectedExtension.events ?? []).map((event) => event.handle),
        selectedDeathHandle,
    ]);
    // Read from the raw handle array so this works whether the family came from the
    // person's extend (not recursively extended) or from its own fetch.
    const familyEventHandles = ownFamilies.flatMap((family) => family.event_ref_list ?? []).map((ref) => ref.ref);

    // Independent of each other: the selected person's own/family events do not
    // need the relatives, and the relatives (fetched here so ownFamilies' events
    // can resolve a spouse's name) do not need the events. `parent_family_list` is
    // extended too so a spouse's own parent family -- and so who their parents (the
    // beaux-parents) are -- comes back embedded, letting loadRelatedDrawerEntries
    // resolve them without a dedicated family fetch of its own.
    const [relatives, events] = await Promise.all([
        fetchPeopleByHandles(relativeHandles, token, 'event_ref_list,parent_family_list'),
        // With their participants, so each event can list its witnesses.
        fetchEventsByHandles(unique([...selectedEventHandles, ...familyEventHandles]), token, { participants: true }),
    ]);

    const eventsByHandle = new Map(events.filter((event) => event.handle).map((event) => [event.handle!, event]));
    const selectedDeathEvent = selectedDeathHandle ? eventsByHandle.get(selectedDeathHandle) : undefined;
    const selectedEvents = selectedEventHandles.flatMap((handle) => {
        const event = attendedRoles.has(handle) ? undefined : eventsByHandle.get(handle);
        return event ? [event] : [];
    });
    const birthDate = restDateToJsDate(selectedEvents.find((event) => event.type === 'Birth')?.date);
    const personalDeathEvent = selectedDeathEvent ?? (selectedDeathHandle ? eventsByHandle.get(selectedDeathHandle) : undefined);
    const deathDate = restDateToJsDate(personalDeathEvent?.date ?? selectedEvents.find((event) => event.type === 'Death')?.date);
    const entries: EventEntry[] = [];
    const add = (entry: EventEntry) => {
        if (entry.event.handle && !entries.some((item) => item.event.handle === entry.event.handle)) entries.push(entry);
    };
    for (const event of selectedEvents) {
        add({ event, who: 'own', isFamily: false });
    }
    if (personalDeathEvent) {
        add({ event: personalDeathEvent, who: 'own', isFamily: false });
    }
    const relativesByHandle = new Map([...relatives, main, selected].filter((person) => person.handle).map((person) => [person.handle!, person]));
    for (const family of ownFamilies) {
        const otherHandle = family.father_handle === selected.handle ? family.mother_handle : family.father_handle;
        for (const ref of family.event_ref_list ?? []) {
            const event = ref.ref ? eventsByHandle.get(ref.ref) : undefined;
            if (event && eventOccursDuringLife(event, birthDate, deathDate)) {
                add({ event, who: 'own', isFamily: true, isSubjectFamily: true, relatedPerson: otherHandle ? relativesByHandle.get(otherHandle) : undefined });
            }
        }
    }

    // The relation is only given when the principal is among the relatives already
    // loaded: asking Gramps would cost one slow request per attended event.
    const attendedEntries: AttendedEntry[] = [...attendedRoles].flatMap(([handle, role]) => {
        const event = eventsByHandle.get(handle);
        if (!event) return [];
        const principals = restEventPrincipals(event).map((person) => {
            const relative = relativesByHandle.get(person.handle);
            return { person, relation: relative ? relationshipLabel(selected, relative, localFamilies) : undefined };
        });
        return [{ event, role, principals }];
    }).sort((first, second) => (restDateToJsDate(first.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY)
        - (restDateToJsDate(second.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY));

    // Citations and their sources are resolved lazily by RestEventItem when the reader
    // expands an event, and the relationship label by its own query — both stay off
    // this critical path.
    //
    // `main`/`selected` travel with the snapshot so that, while keepPreviousData is
    // serving the previous person's details, the header cannot render the new person
    // above the old person's events. `birthDate`/`deathDate`/`ownChildrenHandles`/
    // `siblingHandles` travel too, purely so `loadRelatedDrawerEntries` does not have
    // to recompute them from the same inputs.
    return { main, selected, relatives, localFamilies, entries, attendedEntries, selectedDeathEvent: personalDeathEvent, birthDate, deathDate, ownChildrenHandles, siblingHandles };
}

/**
 * The slow half: relatives' deaths, and births for the selected person's own
 * children — each needing its own event fetched with place/notes/media, a third,
 * dependent round trip that only knows which handles to ask for once
 * `loadOwnDrawerDetails`'s relatives have resolved. Runs after, and the caller
 * merges the result into the drawer once it lands, instead of delaying the
 * first paint on it.
 */
async function loadRelatedDrawerEntries(queryClient: QueryClient, treeName: string | null, mainId: string, selectedId: string, token: string | undefined): Promise<EventEntry[]> {
    const own = await queryClient.fetchQuery({
        queryKey: ownDetailsQueryKey(treeName, mainId, selectedId),
        queryFn: () => loadOwnDrawerDetails(queryClient, treeName, mainId, selectedId, token),
    });
    if (!own) return [];
    const { selected, relatives, localFamilies, birthDate, deathDate, ownChildrenHandles, siblingHandles } = own;

    // Beaux-parents (a spouse's own parents) aren't in `relatives`: that list only goes
    // one hop from the selected person's own two family lists (their parents/siblings,
    // and their spouse/children), never past the spouse to *their* parent family. The
    // spouse's `parent_family_list` was already extended onto `relatives` above, so who
    // their parents are is known here for free -- fetching the beaux-parents themselves
    // (their name, gender, death event) is still a genuinely new set of people, though,
    // and needs its own request.
    const ownFamilies = localFamilies.filter((family) => family.father_handle === selected.handle || family.mother_handle === selected.handle);
    const relativesByHandle = new Map(relatives.filter((person) => person.handle).map((person) => [person.handle!, person]));
    const spouses = ownFamilies.flatMap((family) => {
        const spouseHandle = family.father_handle === selected.handle ? family.mother_handle : family.father_handle;
        const spouse = spouseHandle ? relativesByHandle.get(spouseHandle) : undefined;
        return spouse ? [spouse] : [];
    });
    const spouseParentFamilies = spouses.flatMap((spouse) => personExtended(spouse).parent_families ?? []);
    const belleFamilleHandles = unique(spouseParentFamilies.flatMap((family) => [family.father_handle, family.mother_handle]))
        .filter((handle) => !relativesByHandle.has(handle));
    const belleFamille = await fetchPeopleByHandles(belleFamilleHandles, token);
    const belleFamilleLabelByHandle = new Map(belleFamille.flatMap((person) => person.handle
        ? [[person.handle, restPersonIsMale(person) ? 'son beau-père' : 'sa belle-mère'] as const]
        : []));
    const allRelatives = [...relatives, ...belleFamille];

    // Small: a handful of deaths plus one birth per child/sibling of the selected person.
    const relatedDeathRefs = allRelatives.flatMap((person) => {
        const handle = deathEventHandle(person);
        return handle ? [{ handle, person }] : [];
    });
    const relatedBirthRefs = relatives.flatMap((person) => {
        if (!person.handle || (!ownChildrenHandles.has(person.handle) && !siblingHandles.has(person.handle))) return [];
        const handle = birthEventHandle(person);
        return handle ? [{ handle, person }] : [];
    });
    const relatedBaptismRefs = relatives.flatMap((person) => {
        if (!person.handle || (!ownChildrenHandles.has(person.handle) && !siblingHandles.has(person.handle))) return [];
        const handle = baptismEventHandle(person);
        return handle ? [{ handle, person }] : [];
    });
    const relatedEvents = await fetchEventsByHandles(unique([...relatedDeathRefs.map((ref) => ref.handle), ...relatedBirthRefs.map((ref) => ref.handle), ...relatedBaptismRefs.map((ref) => ref.handle)]), token, { participants: true });
    const eventsByHandle = new Map(relatedEvents.filter((event) => event.handle).map((event) => [event.handle!, event]));

    const entries: EventEntry[] = [];
    const add = (entry: EventEntry) => {
        if (entry.event.handle && !entries.some((item) => item.event.handle === entry.event.handle)) entries.push(entry);
    };
    for (const { handle, person } of relatedDeathRefs) {
        const event = eventsByHandle.get(handle);
        if (event?.type !== 'Death' || !eventOccursDuringLife(event, birthDate, deathDate)) continue;
        const isChild = localFamilies.some((family) => family.child_ref_list?.some((child) => child.ref === person.handle));
        add({
            event,
            who: isChild ? 'child' : 'own',
            isFamily: !isChild,
            relatedPerson: person,
            relatedLabel: (person.handle && belleFamilleLabelByHandle.get(person.handle)) || relationshipLabel(selected, person, localFamilies),
        });
    }
    // A child's birth is as much a waypoint in the subject's own life as a marriage
    // is: isSubjectFamily lets it onto the map alongside the subject's own events, and
    // keeps it showing regardless of the "Événements des proches" toggle, instead of
    // being dropped like the other relatives' events above. A child's baptism is the
    // same kind of waypoint for the parent, so it gets the same treatment. A sibling's
    // birth/baptism is not a waypoint in the subject's own life, though, so it's left
    // as an ordinary relative event -- toggled and mapped like their death already is.
    for (const { handle, person } of relatedBirthRefs) {
        const event = eventsByHandle.get(handle);
        if (event?.type !== 'Birth' || !eventOccursDuringLife(event, birthDate, deathDate)) continue;
        const isOwnChild = !!person.handle && ownChildrenHandles.has(person.handle);
        add({
            event,
            who: 'child',
            isFamily: false,
            isSubjectFamily: isOwnChild,
            relatedPerson: person,
            relatedLabel: relationshipLabel(selected, person, localFamilies),
        });
    }
    for (const { handle, person } of relatedBaptismRefs) {
        const event = eventsByHandle.get(handle);
        if (event?.type !== 'Baptism' || !eventOccursDuringLife(event, birthDate, deathDate)) continue;
        const isOwnChild = !!person.handle && ownChildrenHandles.has(person.handle);
        add({
            event,
            who: 'child',
            isFamily: false,
            isSubjectFamily: isOwnChild,
            relatedPerson: person,
            relatedLabel: relationshipLabel(selected, person, localFamilies),
        });
    }

    return entries;
}

function RestEventDrawer({ mainId, selectedId }: { mainId: string; selectedId: string }) {
    const { setMainId, setSideItemId, treeName } = useViewRoute();
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const queryClient = useQueryClient();
    const [showRelativesEvents, setShowRelativesEvents] = useState(true);
    const [focusedPlace, setFocusedPlace] = useState<[number, number] | undefined>(undefined);
    const [focusedPlaceSelectedId, setFocusedPlaceSelectedId] = useState(selectedId);
    if (selectedId !== focusedPlaceSelectedId) {
        setFocusedPlaceSelectedId(selectedId);
        setFocusedPlace(undefined);
    }

    // Same query keys as the ascendency and map tabs: opening the drawer over a tab
    // that already loaded them costs nothing.
    // keepPreviousData on both: without it, selecting a person who is not yet cached
    // puts these queries back into the loading state, the guard below returns a bare
    // spinner, and the whole subtree — MapTiles included — unmounts.
    const mainQuery = useQuery({
        queryKey: personQueryKey(treeName, mainId),
        queryFn: () => fetchPersonByGrampsId(mainId, token),
        placeholderData: keepPreviousData,
    });
    const selectedQuery = useQuery({
        queryKey: personQueryKey(treeName, selectedId),
        queryFn: () => fetchPersonByGrampsId(selectedId, token),
        placeholderData: keepPreviousData,
    });
    const ancestorPeopleQuery = useAncestorPersons(mainId, treeName);
    const ancestorFamiliesQuery = useAncestorFamilies(mainId, treeName);

    // While keepPreviousData is in effect these still hold the *previous* person, so
    // check identity before using them: otherwise the queries below fire with the old
    // person and cache the result under the new person's key.
    const main = mainQuery.data?.gramps_id === mainId ? mainQuery.data : undefined;
    const selected = selectedQuery.data?.gramps_id === selectedId ? selectedQuery.data : undefined;

    const detailsQuery = useQuery({
        queryKey: ownDetailsQueryKey(treeName, mainId, selectedId),
        queryFn: () => loadOwnDrawerDetails(queryClient, treeName, mainId, selectedId, token),
        enabled: !!main && !!selected,
        placeholderData: keepPreviousData,
    });

    // The slower half (relatives' deaths, children's births) merges in once it
    // lands — not gated behind keepPreviousData, so switching to a person whose
    // related entries were never fetched shows the fast half alone rather than
    // flashing the previous person's relatives against the new person's timeline.
    const relatedEntriesQuery = useQuery({
        queryKey: relatedEntriesQueryKey(treeName, mainId, selectedId),
        queryFn: () => loadRelatedDrawerEntries(queryClient, treeName, mainId, selectedId, token),
        enabled: !!main && !!selected,
    });

    // ~500 ms of server-side graph search for one line of text: let the drawer render
    // without it and fill it in when it lands.
    const relationshipQuery = useQuery({
        queryKey: ['relationship', treeName, mainId, selectedId],
        queryFn: () => fetchRelationship(main!.handle!, selected!.handle!, token),
        enabled: !!main?.handle && !!selected?.handle && mainId !== selectedId,
    });

    const data = useMemo(() => {
        const details = detailsQuery.data;
        if (!details) return undefined;
        const entries = [...details.entries, ...(relatedEntriesQuery.data ?? [])];
        const people = new Map([...(ancestorPeopleQuery.data ?? []), details.main, details.selected, ...details.relatives]
            .filter((person) => person.handle).map((person) => [person.handle!, person]));
        const families = [...new Map([...(ancestorFamiliesQuery.data ?? []), ...details.localFamilies]
            .filter((family) => family.handle).map((family) => [family.handle!, family])).values()];
        return { ...details, entries, people, families };
    }, [detailsQuery.data, relatedEntriesQuery.data, ancestorPeopleQuery.data, ancestorFamiliesQuery.data]);

    // A settled lookup that matched nothing means the requested person does not exist.
    // Without this the drawer would keep showing the previous person's placeholder data.
    const personMissing = (!mainQuery.isFetching && !main) || (!selectedQuery.isFetching && !selected);

    const query = {
        // Only the very first load may replace the drawer with a spinner. Every later
        // change keeps the previous content on screen under the overlay below.
        isLoading: !data && (mainQuery.isLoading || selectedQuery.isLoading || detailsQuery.isLoading),
        isError: mainQuery.isError || selectedQuery.isError || detailsQuery.isError || personMissing,
        isFetching: mainQuery.isFetching || selectedQuery.isFetching || detailsQuery.isFetching
            || ancestorPeopleQuery.isFetching || ancestorFamiliesQuery.isFetching,
    };
    const handleMapLoaded = useCallback((map: mapboxgl.Map) => {
        map.on('click', (event) => {
            const feature = event.target.queryRenderedFeatures(event.point, { layers: ['markers'] })[0];
            if (feature?.geometry.type === 'Point') {
                map.flyTo({ center: feature.geometry.coordinates as [number, number] });
            }
        });
    }, []);

    // Where the person was: their own events, and those they attended as a
    // witness, informant or godparent, which put them there just as surely.
    const geoEntries = useMemo(() => {
        if (!data) return [];
        const eventTitle = (event: RestEvent) =>
            (event.type ? frenchEvents[event.type as keyof typeof frenchEvents] : undefined) ?? event.type ?? '';
        const own = data.entries
            .filter((entry) => !isRelativeEvent(entry))
            .map((entry) => ({ event: entry.event, title: eventTitle(entry.event) }));
        const attended = data.attendedEntries.map(({ event, role }) => ({
            event,
            title: `${attendanceLabel(role, data.selected)} · ${eventTitle(event)}`,
        }));
        return [...own, ...attended].flatMap((item) => {
            const coordinates = eventCoordinates(item.event);
            return coordinates ? [{ ...item, coordinates }] : [];
        });
    }, [data]);
    const features = useMemo<GeoJSON.Feature[]>(() => {
        const orderedEntries = [...geoEntries].sort((first, second) => {
            const firstDate = restDateToJsDate(first.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY;
            const secondDate = restDateToJsDate(second.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY;
            return firstDate - secondDate;
        });
        const lines = curvedStepFeatures(
            orderedEntries.map(({ coordinates }) => coordinates),
            { title: data ? restPersonName(data.selected) : '' },
        );
        const points = orderedEntries.map(({ title, coordinates }) => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates },
            properties: { title },
        }));
        return [...lines, ...points];
    }, [data, geoEntries]);
    const bounds = useMemo(() => boundsOfFeatures(features), [features]);

    if (query.isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
    if (query.isError || !data) return <Box sx={{ p: 2 }}><Typography color="error">Impossible de charger les événements.</Typography></Box>;

    const selectedExtension = personExtended(data.selected);
    const attendedHandles = new Set(data.attendedEntries.map((entry) => entry.event.handle));
    // The person's own events: a birth they only witnessed is not theirs.
    const ownExtendedEvents = (selectedExtension.events ?? []).filter((event) => !attendedHandles.has(event.handle));
    const personNotes = selectedExtension.notes ?? [];
    const parentFamily = bestParentFamily(data.selected, data.localFamilies);
    const parentHandles = [parentFamily?.father_handle, parentFamily?.mother_handle];
    const birthDate = restDateToJsDate(ownExtendedEvents.find((event) => event.type === 'Birth')?.date);
    const displayEntries = [
        ...data.entries.filter((entry) => entry.event.handle !== data.selectedDeathEvent?.handle),
        ...(data.selectedDeathEvent ? [{ event: data.selectedDeathEvent, who: 'own' as const, isFamily: false }] : []),
    ].sort((first, second) => {
        const firstDate = restDateToJsDate(first.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY;
        const secondDate = restDateToJsDate(second.event.date)?.getTime() ?? Number.NEGATIVE_INFINITY;
        return firstDate - secondDate;
    });

    return <Box sx={{ position: 'relative' }}>
        {query.isFetching && <Box sx={{ position: 'absolute', inset: 0, zIndex: 4, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 3, pointerEvents: 'none', backgroundColor: 'rgba(255, 255, 255, 0.45)' }}><CircularProgress size={28} /></Box>}
        <Box sx={{ display: { xs: 'contents', sm: 'block' }, p: '75px 10px 0', position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
            <Box sx={{ p: { xs: '4px 10px 0', sm: 0 }, position: { xs: 'sticky', sm: 'static' }, top: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
                <Grid container spacing={2}>
                    {parentHandles.slice(0, 1).map((handle, index) => <Grid key={index} size={5.3} sx={{ textAlign: 'center' }}>
                        {renderParentButton(handle, data.people, setSideItemId)}
                    </Grid>)}
                    <Grid size={1.4} sx={{ textAlign: 'center' }}>{showBackendLinks && parentFamily?.gramps_id && <Link href={backendUrl('family', parentFamily.gramps_id)} target="_blank" rel="noreferrer" sx={{ fontSize: '0.75rem' }}>{parentFamily.gramps_id}</Link>}</Grid>
                    {parentHandles.slice(1).map((handle, index) => <Grid key={index + 1} size={5.3} sx={{ textAlign: 'center' }}>
                        {renderParentButton(handle, data.people, setSideItemId)}
                    </Grid>)}
                </Grid>
                <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <Typography variant="h6" component="span" sx={{ m: 1 }}>
                        {restPersonName(data.selected)}
                        {' '}({restEventDateString(ownExtendedEvents.find((event) => event.type === 'Birth')?.date)} - {restEventDateString(ownExtendedEvents.find((event) => event.type === 'Death')?.date)})
                    </Typography>
                    {showBackendLinks && <>{' '}<Link href={backendUrl('person', data.selected.gramps_id ?? selectedId)} target="_blank" rel="noreferrer" sx={{ fontSize: '0.75rem' }}>{data.selected.gramps_id}</Link></>}
                </Box>
                {relationshipQuery.data ? <>{relationshipQuery.data} de {restPersonName(data.main)}</> : ''}
            </Box>
            {(selectedExtension.media ?? []).length > 0 && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}><MediaList medias={mapRestMedia(selectedExtension.media)} thumbSizePx={120} /></Box>}
            <Box sx={{ height: isMobile ? 230 : 300, width: '100%', position: 'relative', zIndex: 0 }}>
                {/* Always mounted: unmounting would destroy and rebuild the Mapbox GL
                    instance on every selection change. Hidden rather than removed when
                    the selected person has no geolocated event. */}
                <Box sx={{ height: '100%', visibility: features.length > 0 ? 'visible' : 'hidden' }}>
                    <MapTiles features={features} bounds={bounds} center={focusedPlace} zoom={focusedPlace ? 12 : undefined} onLoaded={handleMapLoaded} />
                </Box>
                {features.length === 0 && <Box sx={{ position: 'absolute', inset: 0 }}>Aucun événement géographique</Box>}
            </Box>
        </Box>
        <PersonStories treeName={treeName} grampsId={data.selected.gramps_id} />
        {personNotes.length > 0 && <Box sx={{ px: isMobile ? 1 : 0, pb: 1 }}><Accordion disableGutters elevation={0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Badge badgeContent={personNotes.length} color="secondary"><SubjectIcon fontSize="small" /></Badge><Typography sx={{ ml: 1 }}>Notes</Typography></AccordionSummary><AccordionDetails><List dense>{personNotes.map((note) => <NoteItem key={note.handle} note={toGrampsNote(note)} />)}</List></AccordionDetails></Accordion></Box>}
        <Divider />
        <List>
            <ListItem>
                <FormControlLabel control={<Switch checked={showRelativesEvents} onChange={(event) => setShowRelativesEvents(event.target.checked)} />} label="Événements des proches" />
            </ListItem>
            <Divider />
            {displayEntries.map((entry) => {
                const extension = eventExtended(entry.event);
                return <Collapse key={entry.event.handle} in={showRelativesEvents || !isRelativeEvent(entry)} timeout="auto" unmountOnExit>
                    <RestEventItem event={entry.event} who={entry.who} isFamily={entry.isFamily} relatedPerson={entry.relatedPerson} relatedLabel={entry.relatedLabel} personBirthDate={birthDate} place={extension.place} media={mapRestMedia(extension.media)} notes={extension.notes ?? []} onPlaceClick={setFocusedPlace} />
                </Collapse>;
            })}
            {ATTENDANCE_ROLES.map((role) => {
                const attended = data.attendedEntries.filter((entry) => entry.role === role);
                return attended.length > 0 && <Box key={role}>
                    <Divider />
                    <ListItem><Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>{attendedSectionTitle(role, data.selected)}</Typography></ListItem>
                    {attended.map(({ event, principals }) => {
                        const extension = eventExtended(event);
                        return <RestEventItem key={event.handle} event={event} who="own" isFamily={false} principals={principals} viewerHandle={data.selected.handle} personBirthDate={birthDate} place={extension.place} media={mapRestMedia(extension.media)} notes={extension.notes ?? []} onPlaceClick={setFocusedPlace} />;
                    })}
                </Box>;
            })}
            <Divider />
            <ListItem sx={{ justifyContent: 'center' }}><Button onClick={() => data.selected.gramps_id && setMainId(data.selected.gramps_id)} variant="contained">Centrer sur cette personne</Button></ListItem>
        </List>
    </Box>;
}

export function MobileCompatDrawer() {
    const { mainId, sideItemId, storyId, treeName } = useViewRoute();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(true);
    const lastCloseAt = useRef(0);
    const handleRef = useRef<HTMLDivElement>(null);
    const { drawerWidth, startResize } = useDrawerResize({ initialWidth: getDefaultLeftPaneWidth(), minWidth: MIN_LEFT_PANE_WIDTH, maxWidthRatio: MAX_LEFT_PANE_WIDTH_RATIO, storageKey: 'eventDrawerWidth' });

    useEffect(() => {
        if (isMobile) {
            document.documentElement.style.removeProperty('--event-drawer-width');
            return;
        }
        document.documentElement.style.setProperty('--event-drawer-width', `${drawerWidth}px`);
        return () => {
            document.documentElement.style.removeProperty('--event-drawer-width');
        };
    }, [drawerWidth, isMobile]);

    const selectedId = sideItemId ?? mainId;
    // Over a story, each link the reader follows opens the sheet again, even
    // after they swiped the previous person away.
    const [openedFor, setOpenedFor] = useState(selectedId);
    if (selectedId !== openedFor) {
        setOpenedFor(selectedId);
        if (storyId) setOpen(true);
    }
    if (!selectedId) return null;
    // On a person's page there is no main person yet: the selected one stands
    // in, which only leaves out the "X de Y" relationship line.
    const content = <RestEventDrawer mainId={mainId ?? selectedId} selectedId={selectedId} />;

    if (isMobile) return <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={() => { lastCloseAt.current = performance.now(); setOpen(false); }}
        onOpen={() => { if (performance.now() - lastCloseAt.current >= 220) setOpen(true); }}
        swipeAreaWidth={COLLAPSED_HEIGHT}
        disableDiscovery
        allowSwipeInChildren={(event: TouchEvent) => handleRef.current?.contains(event.target as Node) ?? false}
        slotProps={{ paper: { sx: { top: `calc(${MOBILE_TOOLBAR_HEIGHT}px + ${MOBILE_OPEN_GAP}px + ${COLLAPSED_HEIGHT}px)`, height: `calc(100dvh - ${MOBILE_TOOLBAR_HEIGHT}px - ${MOBILE_OPEN_GAP}px - ${COLLAPSED_HEIGHT}px)`, overflow: 'visible' } } }}
    >
        {/* Bled above the Paper's own box via a negative offset so it stays within the
            viewport (flush with the bottom edge) even when the sheet is translated fully
            out of view, giving mobile users a permanent handle to reopen the drawer.
            Once the close transition ends, MUI's Modal sets visibility: hidden on its root
            regardless of keepMounted, and the Paper gets pointer-events: none — both
            inherited by this box, so they're explicitly re-declared here to opt back in.
            It also sits inside the Paper's DOM subtree, so swipe-to-open (which by default
            only arms on the invisible SwipeArea) needs allowSwipeInChildren scoped to this
            handle to keep working now that it visually covers that area when closed. */}
        <Box ref={handleRef} onClick={() => setOpen(true)} sx={{ position: 'absolute', top: -COLLAPSED_HEIGHT, left: 0, right: 0, height: COLLAPSED_HEIGHT, backgroundColor: 'background.paper', borderTopLeftRadius: SHEET_RADIUS, borderTopRightRadius: SHEET_RADIUS, boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.18)', borderTop: 1, borderColor: 'divider', visibility: 'visible', pointerEvents: 'auto' }}>
            <Puller />
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>{content}</Box>
    </SwipeableDrawer>;
    // Over a story, the drawer closes back to the text alone.
    const close = storyId && treeName
        ? <IconButton
            aria-label="Fermer"
            size="small"
            onClick={() => void navigate(getStoryPath(storyId, treeName))}
            sx={{ position: 'fixed', top: 72, left: drawerWidth - 48, zIndex: 1301, backgroundColor: 'background.paper', boxShadow: 1, '&:hover': { backgroundColor: 'background.paper' } }}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
        : null;
    return <>{close}<Drawer variant="permanent" anchor="left" sx={{ width: drawerWidth, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }}>{content}</Drawer><Box onMouseDown={startResize} sx={{ position: 'fixed', top: 0, left: drawerWidth - 5, width: 10, height: '100vh', cursor: 'col-resize', zIndex: 1300, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }} /></>;
}
